import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { CheckCircle2, Loader2, XCircle, Clock, Download, Gift } from "lucide-react";

const MAX_ATTEMPTS = 8;
const INTERVAL = 2000;

export default function GiftCardSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [gc, setGc] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/giftcards/status/${sessionId}`);
        if (cancelled) return;
        setGc(data.giftcard);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("error"); return; }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) { setState("pending"); return; }
        setTimeout(poll, INTERVAL);
      } catch {
        if (cancelled) return;
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) { setState("error"); return; }
        setTimeout(poll, INTERVAL);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-24" data-testid="giftcard-success-page">
      <div className="max-w-md w-full rounded-3xl bg-white border border-black/5 shadow-[0_16px_48px_rgba(44,48,48,0.1)] p-8 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-brand mb-4 animate-spin" strokeWidth={1.5} />
            <p className="text-ink-600">{t("giftSuccess.checking")}</p>
          </>
        )}

        {state === "paid" && gc && (
          <div data-testid="giftcard-success-paid">
            <div className="mx-auto w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center mb-4">
              <Gift size={30} className="text-brand" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-3xl text-ink-900 mb-2">{t("giftSuccess.title")}</h1>
            <p className="text-ink-600 mb-5">{t("giftSuccess.thanks")}</p>
            <div className="rounded-xl bg-cream border border-black/5 p-4 text-left text-sm space-y-1.5 mb-6">
              <div className="flex justify-between"><span className="text-ink-400">{t("giftSuccess.amount")}</span><span className="text-ink-900 font-medium">{gc.amount} €</span></div>
              {gc.recipient_name && <div className="flex justify-between"><span className="text-ink-400">{t("giftSuccess.recipient")}</span><span className="text-ink-900">{gc.recipient_name}</span></div>}
              <div className="flex justify-between"><span className="text-ink-400">{t("giftSuccess.code")}</span><span className="text-ink-900 font-mono font-medium">{gc.code}</span></div>
              {gc.expires_at && <div className="flex justify-between"><span className="text-ink-400">{t("giftSuccess.validUntil")}</span><span className="text-ink-900">{gc.expires_at}</span></div>}
            </div>
            <a href={`${API}/giftcards/pdf/${gc.code}`} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-6 py-3 font-medium hover:bg-brand-600 transition-colors" data-testid="gift-download-pdf">
              <Download size={18} /> {t("giftSuccess.download")}
            </a>
            <p className="mt-3 text-xs text-ink-400">{t("giftSuccess.emailNote")}</p>
            <Link to="/" className="mt-4 inline-block text-sm text-brand hover:underline">{t("giftSuccess.home")}</Link>
          </div>
        )}

        {state === "pending" && (
          <>
            <Clock size={48} className="mx-auto text-amber-500 mb-4" strokeWidth={1.5} />
            <p className="text-ink-600 mb-6">{t("giftSuccess.pending")}</p>
            <Link to="/" className="inline-block rounded-full border border-ink-400/30 text-ink-700 px-6 py-3 font-medium">{t("giftSuccess.home")}</Link>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-red-500 mb-4" strokeWidth={1.5} />
            <p className="text-ink-600 mb-6">{t("giftSuccess.error")}</p>
            <Link to="/" className="inline-block rounded-full bg-brand text-white px-6 py-3 font-medium hover:bg-brand-600 transition-colors">{t("giftSuccess.home")}</Link>
          </>
        )}
      </div>
    </div>
  );
}
